<template>
  <div class="rounded-t-2xl overflow-hidden text-light-950 dark:text-dark-50 dark:dark:bg-dark-800">
    <CarouselSteps :order="Object.entries(steps).sort((a, b) => a[1].order - b[1].order).map(s => s[0])" v-model:step="step">
      <template v-for="(s, stepId, i) in steps" #[stepId]>
        <component
          :is="s.component"
          :event="event"
          :group="group"
          :organization="organization"
          :form-data="formData"
          :error="error"
          :on-end="onEnd"
          @reset="onReset"
          @update="(v: any) => onUpdate(v)"
          @error="(v: Err) => error = v"
          @refresh="() => emit('refresh')"
          @next="() => onStep(1)"
          @previous="() => onStep(-1)"
          v-if="step === i"
        />
      </template>
    </CarouselSteps>
  </div>
</template>

<script lang="ts" setup>
import UserStore from '@/store/user'
import EventStore from '@/store/events'
import PlacesStore from '@/store/places'
import OrganizationStore from '@/store/organization'
import GroupsStore from '@/store/groups'
import { Err, Event } from '@/classes'
import { Capacitor } from '@capacitor/core'

const props = defineProps<{
  event: Event
  isPending: boolean
}>()

const emit = defineEmits<{
  (e: 'refresh'): void
}>()

const { event } = toRefs(props)
const route = useRoute()

const error: Ref<Err | null> = ref(null)

const eventStore = EventStore()
const placesStore = PlacesStore()
const groupStore = GroupsStore()
const userStore = UserStore()
const organizationStore = OrganizationStore()

const group = computed(() => groupStore.getItem(event.value?.group ?? ''))
const place = computed(() => placesStore.getItem(event.value?.place ?? ''))
const organization = computed(() => organizationStore.getItem(event.value?.organization ?? ''))

const formData: Ref<{
  guests: number
  amount: number
  products: { [key: string]: number }
}> = ref({
  guests: 0,
  amount: 0,
  products: {}
})

const { currentUser } = storeToRefs(userStore)

const step = defineModel('step', { default: 0 })

const onStep = (value: 1 | -1) => {
  if (value === 1) {
    step.value = Object.values(steps.value).findIndex(step => step.isVisible && step.order > stepCurrent.value.order)

    error.value = null
  } else {
    const index = Object.values(steps.value).slice(0, step.value).findLastIndex(step => step.isVisible && step.order < stepCurrent.value.order)

    step.value = index
  }
}

interface Step {
  isVisible: boolean
  component: any
  order: number
}

const stepCurrent = computed(() => Object.values(steps.value)[step.value])

const steps: ComputedRef<{ [key: string]: Step }> = computed(() => ({
  initial: {
    order: 0,
    isVisible: true,
    component: resolveComponent('EventPageStepInitial')
  },

  complete: {
    order: 1,
    isVisible: !currentUser.value?.isOnboarded2,
    component: resolveComponent('EventPageStepProfileComplete')
  },

  validate: {
    order: 1,
    isVisible: event.value.isOrganizing(currentUser.value?._id) && event.value.isValidationNeeded(currentUser.value?._id),
    component: resolveComponent('EventPageStepValidate')
  },

  review: {
    order: 1,
    isVisible: event.value.isReviewNeeded(currentUser.value?._id),
    component: resolveComponent('EventPageStepReview')
  },

  reviewReceived: {
    order: 1,
    isVisible: event.value.isOrganizing(currentUser.value?._id) && event.value.reviews.length > 0,
    component: resolveComponent('EventPageStepReviewReceived')
  },

  info: {
    order: 2,
    isVisible: !(currentUser.value?.journey ?? []).includes('info-attend'),
    component: resolveComponent('EventPageStepInfoAttend')
  },

  place: {
    order: 2,
    isVisible: event.value.isAttended(currentUser.value?._id) && !place.value?.hasReviewed(currentUser.value?._id) && place.value?.isRatingEnabled ? true : false,
    component: resolveComponent('EventPageStepReviewPlace')
  },

  update: {
    order: 2,
    isVisible: event.value.isAttendingOrWaiting(currentUser.value?._id) && !event.value.isAttended(currentUser.value?._id) ? true : false,
    component: resolveComponent('EventPageStepUpdate')
  },

  group: {
    order: 3,
    isVisible: !event.value.isAttendingOrWaiting(currentUser.value?._id) && group.value && !group.value.isMember(currentUser.value?._id) ? true : false,
    component: resolveComponent('EventPageStepGroup')
  },

  guests: {
    order: 4,
    isVisible: !event.value.isAttendingOrWaiting(currentUser.value?._id) && event.value?.maxGuests > 0 && (event.value.products ?? []).length <= 0,
    component: resolveComponent('EventPageStepGuests')
  },

  prices: {
    order: 5,
    isVisible: !event.value.isAttendingOrWaiting(currentUser.value?._id) && (event.value.products ?? []).length > 0,
    component: resolveComponent('EventPageStepProducts')
  },

  payment: {
    order: 6,
    isVisible: !event.value.isAttendingOrWaiting(currentUser.value?._id) && (event.value.products ?? []).length > 0,
    component: Capacitor.getPlatform() === 'web' || Capacitor.getPlatform() === 'android' ?resolveComponent('EventPageStepPayment') : resolveComponent('EventPageStepPaymentApple')
  },

  paymentConfirmation: {
    order: 7,
    isVisible: route.query.payment_intent !== undefined || route.query.token_id !== undefined,
    component: resolveComponent('EventPageStepPaymentConfirmation')
  },

  skip: {
    order: 9,
    isVisible: !event.value.isAttendingOrWaiting(currentUser.value?._id) && event.value?.maxGuests <= 0 && (event.value.products ?? []).length <= 0,
    component: resolveComponent('EventPageStepSkip')
  },

  confirmation: {
    order: 99,
    isVisible: true,
    component: resolveComponent('EventPageStepConfirmation')
  }
}))

const onUpdate = async (params?: {
  guests?: number
  amount?: number
  products?: { [key: string]: number }
}) => {
  if (params?.guests !== undefined) {
    formData.value.guests = params.guests
  }

  if (params?.amount !== undefined) {
    formData.value.amount = params.amount
  }

  if (params?.products) {
    formData.value.products = params.products
  }
}

const onEnd = async () => {
  const lastStep = Object.values(steps.value).findLastIndex(s => s.isVisible && s.order >= stepCurrent.value.order && s.order < 99)

  if (step.value === lastStep) {
    const response = await eventStore.attend({
      event: props.event._id,
      guests: formData.value.guests
    })

    useNuxtApp().$amplitude.logEvent(`Event_Attend`)

    return response.ok
  }

  return true
}

const onReset = () => {
  formData.value.guests = 0
  step.value = 0
}

useKeepAlive({
  onMounted: () => {
    if (route?.query?.payment_intent) step.value = Object.keys(steps.value).findIndex(step => step === 'paymentConfirmation')
  },
  onBeforeUnmount: () => onReset()
})

defineExpose({
  reset: () => onReset()
})
</script>